import {
  formatDate,
  formatFullHouseNumber,
  formatFullName,
  formatPrice,
  getCompanyOrFullName,
  getLandlineOrMobileNumber,
  priceWithoutVat,
} from './formatter';
import useBookingStore from '../store/booking';
import useOrderStore from '../store/order';
import type { Address } from '../types/address';
import type { StepOrderRequestData, StepOrdererData, StepMphData } from '../types/step-data';
import { BookingMode } from '../types/order-main';
import type { Household } from '../types/household';

interface PropsInstallationFields {
  check: boolean;
}

/**
 * GVM-481: New documents force us to use a specific formatting instead
 * of the function from formatter.ts
 * @see https://kmhgmbh.atlassian.net/browse/GVM-481
 */
function getFormOfAddress({ isCompany, salutation }: Pick<Household, 'isCompany' | 'salutation'>): string {
  return isCompany ? 'Firma' : salutation;
}

function buildCustomerFields({
  contact,
  household,
  address,
  invoiceAddress,
}: StepOrdererData): Record<string, unknown> {
  const {
    customerId,
    dateOfBirth,
    isCompany,
    registerNumber,
    salutation,
  } = household;

  const {
    city,
    houseNumber,
    street,
    suffix,
    zipCode,
  } = invoiceAddress ?? address;

  return {
    '1_Anrede': getFormOfAddress(household),
    '1_Name': getCompanyOrFullName(household),
    '1_Kundennummer': customerId ?? '',
    '1_Straße': street,
    '1_Hausnummer': formatFullHouseNumber({ houseNumber, suffix }),
    '1_PLZ': zipCode,
    '1_Ort': city,
    '1_Telefonnummer': getLandlineOrMobileNumber(contact),
    '1_Geburtsdatum': !isCompany && dateOfBirth ? formatDate(dateOfBirth) : '',
    '1_E-Mail-Adresse': contact.email,
    '1_GF-Anrede': isCompany ? `${salutation}` : '',
    '1_GF-Geburtsdatum': isCompany && dateOfBirth ? formatDate(dateOfBirth) : '',
    '1_GF-HR-Nr': isCompany ? registerNumber : '',
    '1_GF-Name': isCompany ? formatFullName(household) : '',
  };
}

function buildBuildingFields(mphData: StepMphData, customerAddress: Address)
: Record<string, unknown> {
  const {
    buildingUnderConstruction,
    buildingUseFromDate,
    numberOfCommercialUnits,
    numberOfFloors,
    numberOfResidentialUnits,
  } = mphData;

  return {
    '2.1_Wohneinheiten': numberOfResidentialUnits || '',
    '2.1_Geschäftseinheiten': numberOfCommercialUnits || '',
    '2.1_Etagen': numberOfFloors || '',
    '2.1_Straße': customerAddress.street,
    '2.1_Hausnummer': formatFullHouseNumber(customerAddress),
    '2.1_PLZ': customerAddress.zipCode,
    '2.1_Ort': customerAddress.city,
    '2.1_Check_Bau': buildingUnderConstruction ? 'Ja' : 'Off',
    '2.1_Erstbezug': buildingUnderConstruction ? formatDate(buildingUseFromDate) : '',
  };
}

function buildAdministrationFields(
  { administrator, isCommunityOfOwners, numberOfOwners }: StepMphData,
): Record<string, unknown> {
  let fields: Record<string, unknown> = {
    '2.2_Eigentum': numberOfOwners > 1 ? 'Nein' : 'Ja',
    '2.2_Anzahl-E': numberOfOwners > 1 ? numberOfOwners - 1 : '',
    '2.2_Wohnungseigentümerschaft': isCommunityOfOwners ? 'Ja' : 'Nein',
  };

  if (administrator?.isAdministrator) {
    const { household, contact, address } = administrator;

    fields = {
      ...fields,
      '2.2_Anrede': getFormOfAddress(household!),
      '2.2_Name': getCompanyOrFullName(household!),
      '2.2_E-Mail-Adresse': contact!.email,
      '2.2_Telefonnummer': getLandlineOrMobileNumber(contact),
      '2.2_PLZ': address!.zipCode,
      '2.2_Straße': address!.street,
      '2.2_Ort': address!.city,
      '2.2_Hausnummer': formatFullHouseNumber(address!),
    };
  }

  return fields;
}

function buildContactPersonFields({ contactPerson }: StepMphData): Record<string, unknown> {
  const { contact, household } = contactPerson;

  return {
    '2.3_Anrede': household ? getFormOfAddress(household) : '',
    '2.3_Name': getCompanyOrFullName(household!),
    '2.3_Telefonnummer': getLandlineOrMobileNumber(contact),
    '2.3_E-Mail-Adresse': contact?.email ?? '',
  };
}

function buildInstallationFields(props: PropsInstallationFields): Record<string, unknown> {
  return {
    '3.1_Check': props.check ? 'Ja' : 'Off',
  };
}

function buildPriceFields(
  { ne4Order, price }: StepOrderRequestData,
  { household }: StepOrdererData,
  { ne4ExtensionPrice }: StepMphData,
  mode: BookingMode,
): Record<string, unknown> {
  const finalPrice = household.isCompany ? +priceWithoutVat(price) : +price;

  return {
    '4_Preis': mode !== BookingMode.ne4Only ? formatPrice(finalPrice) : '',
    '4_Preis-Teilinstallation': ne4Order === 'endAssembly' ? formatPrice(ne4ExtensionPrice) : '',
  };
}

export default function buildHouseConnectionAndInfrastructureFields(): Record<string, unknown> {
  const { currentBookingMode, isCurrentBookingModeAnyHouseConnection } = useBookingStore();
  const { mphData, orderRequestData, ordererData } = useOrderStore();

  return {
    ...buildCustomerFields(ordererData!),

    ...buildBuildingFields(mphData!, ordererData!.address),

    ...buildAdministrationFields(mphData!),

    ...buildContactPersonFields(mphData!),

    ...buildInstallationFields({ check: isCurrentBookingModeAnyHouseConnection }),

    ...buildPriceFields(orderRequestData!, ordererData!, mphData!, currentBookingMode!),

    '5_Check': 'Off',
    '8_Ort_Datum': '',
    '8_Unterschrift': '',
  };
}
